<template>
  <div class="main-panel">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goMaintenance" />
    </div>

    <header class="header">
      <div class="header__left">
        <div class="avatar" aria-hidden="true">C</div>
        <div class="greet">
          <h1>Configuración</h1>
          <p class="sub">Define las unidades visibles en capturas de mantenimiento.</p>
        </div>
      </div>
      <div class="header__right">
        <button class="btn btn--ghost" @click="goMaintenance">Regresar</button>
      </div>
    </header>

    <section class="menu-shell">
      <article
        v-if="canConfigureUnits"
        class="menu-item menu-item--units"
        role="link"
        tabindex="0"
        @click="openUnitsConfig"
        @keyup.enter="openUnitsConfig"
      >
        <div class="menu-item__left">
          <span class="menu-icon">🚚</span>
          <div class="menu-copy">
            <h2>Unidades a considerar</h2>
            <p>Selecciona qué unidades se mostrarán en órdenes, incidentes y reportes de fallas.</p>
          </div>
        </div>
        <button class="btn btn--primary" type="button">Configurar</button>
      </article>
      <article
        v-if="canConfigureOperators"
        class="menu-item menu-item--operators"
        role="link"
        tabindex="0"
        @click="openOperatorsConfig"
        @keyup.enter="openOperatorsConfig"
      >
        <div class="menu-item__left">
          <span class="menu-icon">👷</span>
          <div class="menu-copy">
            <h2>Operadores a considerar</h2>
            <p>Selecciona qué empleados aparecerán cuando se elija operador.</p>
          </div>
        </div>
        <button class="btn btn--primary" type="button">Configurar</button>
      </article>
      <article
        v-if="canConfigureMechanics"
        class="menu-item menu-item--mechanics"
        role="link"
        tabindex="0"
        @click="openMechanicsConfig"
        @keyup.enter="openMechanicsConfig"
      >
        <div class="menu-item__left">
          <span class="menu-icon">🔧</span>
          <div class="menu-copy">
            <h2>Mecánicos a considerar</h2>
            <p>Selecciona qué empleados aparecerán en los campos de mecánico de órdenes.</p>
          </div>
        </div>
        <button class="btn btn--primary" type="button">Configurar</button>
      </article>
      <article
        v-if="canConfigureApprovers"
        class="menu-item menu-item--approvers"
        role="link"
        tabindex="0"
        @click="openApproversConfig"
        @keyup.enter="openApproversConfig"
      >
        <div class="menu-item__left">
          <span class="menu-icon">✅</span>
          <div class="menu-copy">
            <h2>Vo. Bueno a considerar</h2>
            <p>Selecciona qué empleados aparecerán en el dropdown de Vo. Bueno.</p>
          </div>
        </div>
        <button class="btn btn--primary" type="button">Configurar</button>
      </article>
    </section>

    <transition name="modal-fade">
      <div v-if="showUnitsModal" class="modal-overlay" @click.self="closeUnitsConfig">
        <div class="modal-card">
          <div class="modal-head">
            <div>
              <h3>Configuracion de unidades visibles</h3>
              <p class="modal-subtitle">Si marcas todas, se mostrarán todas. Si quitas todas, la lista quedará vacía.</p>
            </div>
            <button class="small-btn" type="button" @click="closeUnitsConfig">Cerrar</button>
          </div>
          <div class="modal-actions">
            <button class="small-btn" type="button" @click="selectAll">Seleccionar todo</button>
            <button class="small-btn" type="button" @click="clearAll">Quitar todo</button>
          </div>
          <div class="options-list">
            <label v-for="unit in units" :key="unit.id" class="option">
              <input
                type="checkbox"
                :checked="selectedUnitIds.includes(unit.id)"
                @change="toggleUnit(unit.id, $event.target.checked)"
              />
              <span>{{ unit.unit_short_id }} - {{ unit.unit_plates || 'SIN PLACAS' }}</span>
            </label>
            <div v-if="!units.length" class="empty-row">No hay unidades disponibles para configurar.</div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showOperatorsModal" class="modal-overlay" @click.self="closeOperatorsConfig">
        <div class="modal-card">
          <div class="modal-head">
            <div>
              <h3>Configuracion de operadores visibles</h3>
              <p class="modal-subtitle">Selecciona empleados para el dropdown de operador.</p>
            </div>
            <button class="small-btn" type="button" @click="closeOperatorsConfig">Cerrar</button>
          </div>
          <div class="modal-actions">
            <button class="small-btn" type="button" @click="selectAllOperators">Seleccionar todo</button>
            <button class="small-btn" type="button" @click="clearAllOperators">Quitar todo</button>
          </div>
          <div class="options-list">
            <label v-for="emp in employees" :key="emp.id" class="option">
              <input
                type="checkbox"
                :checked="selectedEmployeeIds.includes(emp.id)"
                @change="toggleOperator(emp.id, $event.target.checked)"
              />
              <span>{{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}</span>
            </label>
            <div v-if="!employees.length" class="empty-row">No hay empleados disponibles para configurar.</div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showMechanicsModal" class="modal-overlay" @click.self="closeMechanicsConfig">
        <div class="modal-card">
          <div class="modal-head">
            <div>
              <h3>Configuracion de mecánicos visibles</h3>
              <p class="modal-subtitle">Selecciona empleados para los dropdowns de mecánico en órdenes.</p>
            </div>
            <button class="small-btn" type="button" @click="closeMechanicsConfig">Cerrar</button>
          </div>
          <div class="modal-actions">
            <button class="small-btn" type="button" @click="selectAllMechanics">Seleccionar todo</button>
            <button class="small-btn" type="button" @click="clearAllMechanics">Quitar todo</button>
          </div>
          <div class="options-list">
            <label v-for="emp in mechanics" :key="emp.id" class="option">
              <input
                type="checkbox"
                :checked="selectedMechanicIds.includes(emp.id)"
                @change="toggleMechanic(emp.id, $event.target.checked)"
              />
              <span>{{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}</span>
            </label>
            <div v-if="!mechanics.length" class="empty-row">No hay empleados disponibles para configurar.</div>
          </div>
        </div>
      </div>
    </transition>
    <transition name="modal-fade">
      <div v-if="showApproversModal" class="modal-overlay" @click.self="closeApproversConfig">
        <div class="modal-card">
          <div class="modal-head">
            <div>
              <h3>Configuracion de Vo. Bueno visibles</h3>
              <p class="modal-subtitle">Selecciona empleados para el dropdown de Vo. Bueno en órdenes.</p>
            </div>
            <button class="small-btn" type="button" @click="closeApproversConfig">Cerrar</button>
          </div>
          <div class="modal-actions">
            <button class="small-btn" type="button" @click="selectAllApprovers">Seleccionar todo</button>
            <button class="small-btn" type="button" @click="clearAllApprovers">Quitar todo</button>
          </div>
          <div class="options-list">
            <label v-for="emp in approvers" :key="emp.id" class="option">
              <input
                type="checkbox"
                :checked="selectedApproverIds.includes(emp.id)"
                @change="toggleApprover(emp.id, $event.target.checked)"
              />
              <span>{{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}</span>
            </label>
            <div v-if="!approvers.length" class="empty-row">No hay empleados disponibles para configurar.</div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'MaintenanceConfigurationModule',
  data() {
    return {
      showUnitsModal: false,
      showOperatorsModal: false,
      showMechanicsModal: false,
      showApproversModal: false,
      units: [],
      selectedUnitIds: [],
      employees: [],
      selectedEmployeeIds: [],
      mechanics: [],
      selectedMechanicIds: [],
      approvers: [],
      selectedApproverIds: [],
      loadingConfig: false,
      savingConfig: false,
      permissions: [],
    }
  },
  computed: {
    canAccessConfiguration() {
      return this.hasPerm('ui_access_maintenance_configuration')
    },
    canConfigureUnits() {
      return this.hasPerm('ui_access_maintenance_config_units')
    },
    canConfigureOperators() {
      return this.hasPerm('ui_access_maintenance_config_operators')
    },
    canConfigureMechanics() {
      return this.hasPerm('ui_access_maintenance_config_mechanics')
    },
    canConfigureApprovers() {
      return this.hasPerm('ui_access_maintenance_config_operators')
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.permissions = data.permissions || []
        if (!this.canAccessConfiguration) {
          this.$router.push({ name: 'MainPanel' })
        }
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    async fetchUnitsConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.loadingConfig = true
      try {
        const { data } = await axios.get(`${apiDirectory}/maintenance/units-config`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.units = Array.isArray(data?.units) ? data.units : []
        const fromApi = Array.isArray(data?.visible_unit_ids) ? data.visible_unit_ids.map(Number) : []
        this.selectedUnitIds = data?.is_configured ? fromApi : this.units.map((unit) => Number(unit.id))
      } catch (error) {
        this.units = []
        this.selectedUnitIds = []
      } finally {
        this.loadingConfig = false
      }
    },
    openUnitsConfig() {
      this.showUnitsModal = true
      this.fetchUnitsConfig()
    },
    closeUnitsConfig() {
      this.showUnitsModal = false
    },
    openOperatorsConfig() {
      this.showOperatorsModal = true
      this.fetchOperatorsConfig()
    },
    closeOperatorsConfig() {
      this.showOperatorsModal = false
    },
    async saveConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.savingConfig = true
      try {
        await axios.put(
          `${apiDirectory}/maintenance/units-config`,
          { visible_unit_ids: this.selectedUnitIds || [] },
          { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
        )
      } finally {
        this.savingConfig = false
      }
    },
    toggleUnit(unitId, checked) {
      const normalized = Number(unitId)
      if (checked) {
        if (!this.selectedUnitIds.includes(normalized)) {
          this.selectedUnitIds = [...this.selectedUnitIds, normalized]
        }
      } else {
        this.selectedUnitIds = this.selectedUnitIds.filter((id) => id !== normalized)
      }
      this.saveConfig()
    },
    selectAll() {
      this.selectedUnitIds = this.units.map((unit) => Number(unit.id))
      this.saveConfig()
    },
    clearAll() {
      this.selectedUnitIds = []
      this.saveConfig()
    },
    async fetchOperatorsConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.loadingConfig = true
      try {
        const { data } = await axios.get(`${apiDirectory}/maintenance/operators-config`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.employees = Array.isArray(data?.employees) ? data.employees : []
        const fromApi = Array.isArray(data?.visible_employee_ids) ? data.visible_employee_ids.map(Number) : []
        this.selectedEmployeeIds = data?.is_configured ? fromApi : this.employees.map((emp) => Number(emp.id))
      } catch (error) {
        this.employees = []
        this.selectedEmployeeIds = []
      } finally {
        this.loadingConfig = false
      }
    },
    async saveOperatorsConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.savingConfig = true
      try {
        await axios.put(
          `${apiDirectory}/maintenance/operators-config`,
          { visible_employee_ids: this.selectedEmployeeIds || [] },
          { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
        )
      } finally {
        this.savingConfig = false
      }
    },
    toggleOperator(employeeId, checked) {
      const normalized = Number(employeeId)
      if (checked) {
        if (!this.selectedEmployeeIds.includes(normalized)) {
          this.selectedEmployeeIds = [...this.selectedEmployeeIds, normalized]
        }
      } else {
        this.selectedEmployeeIds = this.selectedEmployeeIds.filter((id) => id !== normalized)
      }
      this.saveOperatorsConfig()
    },
    selectAllOperators() {
      this.selectedEmployeeIds = this.employees.map((emp) => Number(emp.id))
      this.saveOperatorsConfig()
    },
    clearAllOperators() {
      this.selectedEmployeeIds = []
      this.saveOperatorsConfig()
    },
    async fetchMechanicsConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.loadingConfig = true
      try {
        const { data } = await axios.get(`${apiDirectory}/maintenance/mechanics-config`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.mechanics = Array.isArray(data?.employees) ? data.employees : []
        const fromApi = Array.isArray(data?.visible_employee_ids) ? data.visible_employee_ids.map(Number) : []
        this.selectedMechanicIds = data?.is_configured ? fromApi : this.mechanics.map((emp) => Number(emp.id))
      } catch (error) {
        this.mechanics = []
        this.selectedMechanicIds = []
      } finally {
        this.loadingConfig = false
      }
    },
    openMechanicsConfig() {
      this.showMechanicsModal = true
      this.fetchMechanicsConfig()
    },
    closeMechanicsConfig() {
      this.showMechanicsModal = false
    },
    openApproversConfig() {
      this.showApproversModal = true
      this.fetchApproversConfig()
    },
    closeApproversConfig() {
      this.showApproversModal = false
    },
    async saveMechanicsConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.savingConfig = true
      try {
        await axios.put(
          `${apiDirectory}/maintenance/mechanics-config`,
          { visible_employee_ids: this.selectedMechanicIds || [] },
          { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
        )
      } finally {
        this.savingConfig = false
      }
    },
    toggleMechanic(employeeId, checked) {
      const normalized = Number(employeeId)
      if (checked) {
        if (!this.selectedMechanicIds.includes(normalized)) {
          this.selectedMechanicIds = [...this.selectedMechanicIds, normalized]
        }
      } else {
        this.selectedMechanicIds = this.selectedMechanicIds.filter((id) => id !== normalized)
      }
      this.saveMechanicsConfig()
    },
    selectAllMechanics() {
      this.selectedMechanicIds = this.mechanics.map((emp) => Number(emp.id))
      this.saveMechanicsConfig()
    },
    clearAllMechanics() {
      this.selectedMechanicIds = []
      this.saveMechanicsConfig()
    },
    async fetchApproversConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.loadingConfig = true
      try {
        const { data } = await axios.get(`${apiDirectory}/maintenance/approvers-config`, {
          headers: { Authorization: `Token ${localStorage.getItem('authToken')}` },
        })
        this.approvers = Array.isArray(data?.employees) ? data.employees : []
        const fromApi = Array.isArray(data?.visible_employee_ids) ? data.visible_employee_ids.map(Number) : []
        this.selectedApproverIds = data?.is_configured ? fromApi : this.approvers.map((emp) => Number(emp.id))
      } catch (error) {
        this.approvers = []
        this.selectedApproverIds = []
      } finally {
        this.loadingConfig = false
      }
    },
    async saveApproversConfig() {
      const apiDirectory = process.env.API_DIRECTORY
      this.savingConfig = true
      try {
        await axios.put(
          `${apiDirectory}/maintenance/approvers-config`,
          { visible_employee_ids: this.selectedApproverIds || [] },
          { headers: { Authorization: `Token ${localStorage.getItem('authToken')}` } }
        )
      } finally {
        this.savingConfig = false
      }
    },
    toggleApprover(employeeId, checked) {
      const normalized = Number(employeeId)
      if (checked) {
        if (!this.selectedApproverIds.includes(normalized)) {
          this.selectedApproverIds = [...this.selectedApproverIds, normalized]
        }
      } else {
        this.selectedApproverIds = this.selectedApproverIds.filter((id) => id !== normalized)
      }
      this.saveApproversConfig()
    },
    selectAllApprovers() {
      this.selectedApproverIds = this.approvers.map((emp) => Number(emp.id))
      this.saveApproversConfig()
    },
    clearAllApprovers() {
      this.selectedApproverIds = []
      this.saveApproversConfig()
    },
    goMaintenance() {
      this.$router.push({ path: '/modulo-mantenimiento' })
    },
  },
}
</script>

<style scoped>
.main-panel { min-height: 100vh; background: #f3f6fd; padding: 12vh 24px 48px; }
.nav-bar-operations { position: absolute; top: 0; left: 0; width: 100vw; height: 10vh; background-color: #ffc434; z-index: 1000; display: flex; align-items: center; padding-left: 16px; }
.logo-button { height: 55%; object-fit: contain; cursor: pointer; }
.header { margin-top: 12px; display: flex; align-items: center; justify-content: space-between; gap: 16px; background: rgba(255, 255, 255, 0.78); border: 1px solid rgba(15, 23, 42, 0.08); border-radius: 16px; padding: 14px 18px; }
.header__left { display: flex; align-items: center; gap: 14px; }
.header__right { display: flex; align-items: center; gap: 8px; }
.avatar { width: 48px; height: 48px; border-radius: 14px; display: grid; place-items: center; background: linear-gradient(135deg, #ffc434, #ffe188); color: #3a2a00; font-weight: 800; }
.greet h1 { font-size: 1.2rem; color: #0f172a; margin: 0; }
.greet .sub { margin: 2px 0 0; color: #667085; font-size: .92rem; }
.menu-shell { max-width: 860px; margin: 16px auto 0; display: grid; gap: 12px; }
.menu-item { border-radius: 16px; border: 1px solid #e5e7eb; background: #fff; box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08); padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 12px; cursor: pointer; transition: transform .16s ease, box-shadow .16s ease; }
.menu-item:hover { transform: translateY(-1px); box-shadow: 0 16px 22px rgba(15, 23, 42, 0.12); }
.menu-item__left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.menu-icon { width: 40px; height: 40px; border-radius: 10px; display: grid; place-items: center; font-size: 1.15rem; flex: 0 0 auto; background: rgba(37, 99, 235, 0.14); }
.menu-copy h2 { margin: 0; color: #0f172a; font-size: 1rem; }
.menu-copy p { margin: 3px 0 0; color: #475467; font-size: 0.9rem; }
.btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 10px; border: 1px solid transparent; cursor: pointer; text-decoration: none; font-weight: 700; transition: all 0.16s ease; }
.btn--primary { background: #2563eb; color: #fff; }
.btn--ghost { background: #fff; border-color: #d0d7de; color: #0f172a; }
.btn--primary:hover { box-shadow: 0 10px 20px rgba(37, 99, 235, 0.35); }
.modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55); z-index: 40; display: flex; align-items: center; justify-content: center; padding: 16px; backdrop-filter: blur(2px); }
.modal-card { width: min(820px, 100%); max-height: 82vh; background: linear-gradient(180deg, #ffffff, #f8fbff); border-radius: 14px; border: 1px solid #d7e3f7; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 24px 46px rgba(2, 6, 23, 0.25); }
.modal-head { padding: 12px; border-bottom: 1px solid #dbe7f7; display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(90deg, #eff6ff, #ecfeff); }
.modal-head h3 { margin: 0; color: #0f172a; }
.modal-subtitle { margin: 2px 0 0; font-size: 0.82rem; color: #475569; }
.modal-actions { display: flex; gap: 8px; padding: 10px 12px; border-bottom: 1px solid #e2e8f0; background: #f8fbff; }
.options-list { overflow: auto; padding: 12px; display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 8px; }
.option { display: flex; align-items: center; gap: 8px; padding: 9px 10px; border: 1px solid #dbe5f3; border-radius: 10px; background: #fff; transition: border-color 0.14s ease, box-shadow 0.14s ease, transform 0.14s ease; }
.option:hover { border-color: #93c5fd; box-shadow: 0 8px 16px rgba(37, 99, 235, 0.12); transform: translateY(-1px); }
.small-btn { border: 1px solid #cbd5e1; background: #fff; border-radius: 8px; padding: 6px 10px; cursor: pointer; font-weight: 600; }
.empty-row { font-size: 0.9rem; color: #64748b; padding: 12px; }
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }
</style>
